import { useCallback, useEffect, useState } from 'react'
import { Loader2, Info } from 'lucide-react'
import { SectionHeader } from '@/components/erp/primitives'
import { Note, Forbidden } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import { useCan } from '@/shared/auth/session'
import {
  listHistoricalElections, getGround,
  type HistoricalElection, type GroundReading, type UnitGround,
} from './api'

/**
 * Electoral History — strongholds, battlegrounds, and where turnout was left on the table.
 *
 * ## The table is sorted by upside, not by size or by name
 *
 * Because there is exactly one decision this screen exists to inform: where a turnout
 * operation buys the most votes. A ward that votes 66% for us and turned out four points below
 * the county is the cheapest block of votes in the campaign — those people already vote for us
 * and simply did not come. Sorting alphabetically would bury them among eighty-four others.
 *
 * ## Upside is only ever counted on friendly ground
 *
 * Driving turnout where the ward votes 70% against you hands the seat to somebody else. The
 * figure is zero for weak and hostile ground on purpose, and that is a claim about strategy,
 * not a gap in the data.
 */
export function HistoryPage() {
  const canRead = useCan('historical:read')

  const [elections, setElections] = useState<HistoricalElection[] | null>(null)
  const [current, setCurrent] = useState<string | null>(null)
  const [ground, setGround] = useState<GroundReading | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    if (!canRead) return
    try {
      const rows = await listHistoricalElections()
      setElections(rows)
      setCurrent((c) => c ?? rows[0]?.id ?? null)
      setError(null)
    } catch (err) {
      setError(explain(err))
      setElections([])
    }
  }, [canRead])

  useEffect(() => { void load() }, [load])

  useEffect(() => {
    if (!current || !elections) return
    // Swing needs an earlier election. The next one down the list is the previous cycle,
    // because the API returns them newest first.
    const idx = elections.findIndex((e) => e.id === current)
    const previous = elections[idx + 1]?.id
    let cancelled = false
    setBusy(true)
    getGround(current, 'GOVERNOR', previous)
      .then((g) => { if (!cancelled) { setGround(g); setError(null) } })
      .catch((err) => { if (!cancelled) setError(explain(err)) })
      .finally(() => { if (!cancelled) setBusy(false) })
    return () => { cancelled = true }
  }, [current, elections])

  if (!canRead) return <Forbidden what="electoral history" />

  const election = elections?.find((e) => e.id === current) ?? null

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Electoral History"
        description="How every station actually voted last time — and where turnout was left on the table."
        right={elections && elections.length > 1 ? (
          <select className="erp-input" value={current ?? ''}
                  onChange={(e) => setCurrent(e.target.value)}>
            {elections.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        ) : undefined}
      />

      {error && <Note tone="bad">{error}</Note>}
      {elections?.length === 0 && (
        <Note tone="info">
          No past results imported. Until one is, every projection in this platform rests on
          canvassing alone — a small and self-selected slice of the register.
        </Note>
      )}

      {/* Provenance first. This is the one table whose entire value is where it came from. */}
      {election && (
        <div className="flex gap-2 text-erp-xs text-muted-foreground border border-border
                        rounded-md p-3">
          <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span><b>Source:</b> {election.sourceNote}</span>
        </div>
      )}

      {busy && !ground && (
        <div className="px-4 py-8 text-erp-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Reading the ground…
        </div>
      )}

      {ground && (
        <>
          <div className="grid gap-3 md:grid-cols-4">
            <Stat label="Turnout" value={`${ground.turnoutPct}%`}
                  hint={`${fmt(ground.cast)} of ${fmt(ground.registered)} registered`} />
            <Stat label="Our share" value={`${ground.ourSharePct}%`}
                  hint="of everyone who voted, county-wide" />
            <Stat label="Wards we hold" value={`${ground.strongholds}`}
                  hint={`${ground.battlegrounds} contestable · ${ground.weak} weak · `
                        + `${ground.hostile} hostile`} />
            <Stat label="Turnout upside" value={fmt(ground.totalTurnoutUpside)}
                  hint="votes available if friendly wards merely matched the county's turnout" />
          </div>

          <div className="grid gap-3 md:grid-cols-4">
            {(['STRONGHOLD', 'BATTLEGROUND', 'WEAK', 'HOSTILE'] as const).map((c) => (
              <div key={c} className={`rounded-md p-3 border ${BAND[c].box}`}>
                <div className={`text-erp-xs font-semibold uppercase tracking-wide ${BAND[c].ink}`}>
                  {BAND[c].label}
                </div>
                <div className="text-erp-xs text-muted-foreground mt-1">{BAND[c].meaning}</div>
              </div>
            ))}
          </div>

          <div className="border border-border rounded-md overflow-hidden">
            <div className="px-4 py-3 border-b border-border">
              <h3 className="text-erp-sm font-semibold">
                Where turnout was left on the table
              </h3>
              <p className="text-erp-xs text-muted-foreground">
                Sorted by what a turnout operation could buy. Upside is counted only on ground
                that already votes for us — driving turnout in a ward that votes against you
                hands the seat to somebody else.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-erp-sm">
                <thead>
                  <tr className="text-erp-xs uppercase text-muted-foreground">
                    <th className="text-left px-4 py-2">Ward</th>
                    <th className="text-left px-3 py-2">Ground</th>
                    <th className="text-right px-3 py-2">Registered</th>
                    <th className="text-right px-3 py-2">Turnout</th>
                    <th className="text-right px-3 py-2">Our share</th>
                    <th className="text-right px-3 py-2">Margin</th>
                    <th className="text-right px-3 py-2">Swing</th>
                    <th className="text-right px-4 py-2">Upside</th>
                  </tr>
                </thead>
                <tbody>
                  {ground.units.map((u) => <Row key={u.unitId} u={u} />)}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

const BAND = {
  STRONGHOLD: {
    label: 'Stronghold', meaning: '55% or more. Protect it and get it out.',
    box: 'border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30',
    ink: 'text-emerald-800 dark:text-emerald-300',
    pill: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200',
  },
  BATTLEGROUND: {
    label: 'Battleground', meaning: '45–55%. Genuinely contestable — where persuasion pays.',
    box: 'border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30',
    ink: 'text-amber-800 dark:text-amber-300',
    pill: 'bg-amber-100 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200',
  },
  WEAK: {
    label: 'Weak', meaning: '30–45%. Winnable over a cycle, not over a weekend.',
    box: 'border-sky-300 dark:border-sky-800 bg-sky-50 dark:bg-sky-950/30',
    ink: 'text-sky-800 dark:text-sky-300',
    pill: 'bg-sky-100 text-sky-900 dark:bg-sky-950/40 dark:text-sky-200',
  },
  HOSTILE: {
    label: 'Hostile', meaning: 'Under 30%. Spending here is spending on somebody else.',
    box: 'border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40',
    ink: 'text-slate-700 dark:text-slate-300',
    pill: 'bg-slate-200 text-slate-800 dark:bg-slate-800 dark:text-slate-200',
  },
} as const

function Row({ u }: { u: UnitGround }) {
  return (
    <tr className="border-t border-border">
      <td className="px-4 py-2">{u.unitName}</td>
      <td className="px-3 py-2">
        <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5
                          rounded ${BAND[u.classification].pill}`}>
          {BAND[u.classification].label}
        </span>
      </td>
      <td className="text-right px-3 py-2 tabular-nums">{fmt(u.registered)}</td>
      <td className="text-right px-3 py-2 tabular-nums">
        {u.turnoutPct}%
        {u.turnoutGapPp > 0 && (
          <span className="text-muted-foreground"> ({u.turnoutGapPp} below)</span>
        )}
      </td>
      <td className="text-right px-3 py-2 tabular-nums">{u.ourSharePct}%</td>
      <td className={`text-right px-3 py-2 tabular-nums ${u.marginPp >= 0
        ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>
        {u.marginPp >= 0 ? '+' : ''}{u.marginPp}
      </td>
      <td className={`text-right px-3 py-2 tabular-nums ${u.swingPp == null ? 'text-muted-foreground'
        : u.swingPp >= 0 ? 'text-emerald-700 dark:text-emerald-300'
        : 'text-red-700 dark:text-red-300'}`}>
        {u.swingPp == null ? '—' : `${u.swingPp >= 0 ? '+' : ''}${u.swingPp}`}
      </td>
      <td className="text-right px-4 py-2 tabular-nums font-medium">
        {u.turnoutUpside > 0 ? fmt(u.turnoutUpside) : <span className="text-muted-foreground">—</span>}
      </td>
    </tr>
  )
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="border border-border rounded-md p-4">
      <div className="text-erp-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold tabular-nums mt-1">{value}</div>
      {hint && <div className="text-erp-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  )
}

const fmt = (n: number) => n.toLocaleString('en-KE')
